<ng-container *transloco="let t">
  <div id="file-upload-container" class="w-full">
    <div
      id="drop-zone"
      class="relative border-2 border-dashed border-ld-neutral-200 rounded-lg p-6 transition-colors cursor-pointer bg-white"
      [class.border-ld-primary]="isDragging()"
      [class.bg-ld-primary-50]="isDragging()"
      [class.hover:border-ld-neutral-400]="canAddMore()"
      [class.opacity-50]="!canAddMore()"
      [class.cursor-not-allowed]="!canAddMore()"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave($event)"
      (drop)="onDrop($event)"
      (click)="fileInput.click()"
      (keydown.enter)="fileInput.click()"
      (keydown.space)="fileInput.click()"
      tabindex="0"
      role="button"
      [attr.aria-label]="canAddMore() ? 'Click or drag files to upload' : 'Maximum files reached'"
    >
      <input
        #fileInput
        type="file"
        [accept]="acceptedTypes()"
        multiple
        class="hidden"
        (change)="onFileSelect($event)"
        [disabled]="!canAddMore()"
      />

      <div class="flex flex-col items-center text-center">
        <mat-icon fontSet="material-icons-outlined" class="w-10! h-10! text-[40px]! text-ld-neutral mb-3">
          file_upload
        </mat-icon>
        @if (canAddMore()) {
          <p class="font-dm text-sm font-semibold text-ld-neutral mb-1">
            {{ t('fileUploadClickHereToUpload') }}
          </p>
        } @else {
          <p class="font-dm text-sm text-ld-neutral-500 mb-1">
            {{ t('fileUploadMaxFilesReached') }}
          </p>
        }
        <p class="font-dm text-xs text-ld-neutral-500">{{ t('fileUploadAllowedTypesDetailed') }}</p>
        <p class="font-dm text-xs text-ld-link italic">{{ t('fileUploadRemovePasswordProtection') }}</p>
      </div>
    </div>

    @if (validationError()) {
      <div id="validation-error" class="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
        <p class="font-dm text-sm text-ld-error">{{ validationError() }}</p>
      </div>
    }

    @if (files().length > 0) {
      <div id="file-list" class="mt-4 space-y-2">
        @for (uploadedFile of files(); track uploadedFile.id; let i = $index) {
          <div
            [id]="'file-item-' + i"
            class="rounded-lg overflow-hidden"
            [class.bg-ld-neutral-50]="!uploadedFile.error"
            [class.bg-red-50]="uploadedFile.error"
            [class.border]="uploadedFile.error"
            [class.border-red-200]="uploadedFile.error"
          >
            <div class="flex items-center gap-3 px-3 py-2">
              <div class="shrink-0">
                @if (uploadedFile.error) {
                  <mat-icon class="w-5! h-5! text-[20px]! text-ld-error">error</mat-icon>
                } @else {
                  <mat-icon class="w-5! h-5! text-[20px]! text-green-600">check_circle</mat-icon>
                }
              </div>

              <div class="flex-1 min-w-0">
                <p class="font-dm text-sm text-ld-neutral truncate" [title]="uploadedFile.file.name">
                  {{ uploadedFile.file.name }}
                </p>
                @if (uploadedFile.error) {
                  <p class="font-dm text-xs text-ld-error">{{ uploadedFile.error }}</p>
                } @else {
                  <p class="font-dm text-xs text-ld-neutral-400">{{ formatFileSize(uploadedFile.file.size) }}</p>
                }
              </div>

              <div class="flex items-center gap-1 shrink-0">
                @if (!uploadedFile.error) {
                  <button
                    type="button"
                    [id]="'file-notes-toggle-' + i"
                    class="font-dm text-sm font-semibold text-ld-link hover:text-ld-link-hover cursor-pointer px-2 py-1"
                    (click)="
                      uploadedFile.showEditNotes ? dismissNotes(uploadedFile.id) : editNotes(uploadedFile.id);
                      $event.stopPropagation()
                    "
                    [attr.aria-label]="
                      (uploadedFile.showEditNotes ? 'Save notes for ' : 'Add notes for ') + uploadedFile.file.name
                    "
                  >
                    {{ uploadedFile.showEditNotes ? t('fileUploadSaveNotes') : t('fileUploadAddNotes') }}
                  </button>

                  @if (isPreviewable(uploadedFile.file)) {
                    <button
                      type="button"
                      [id]="'file-preview-toggle-' + i"
                      class="p-1 transition-opacity hover:opacity-70 cursor-pointer"
                      (click)="togglePreview(uploadedFile.id); $event.stopPropagation()"
                      [attr.aria-label]="
                        (uploadedFile.showPreview ? 'Hide preview for ' : 'Preview ') + uploadedFile.file.name
                      "
                    >
                      <img [src]="uploadedFile.showPreview ? eyeSlashIcon : eyeIcon" alt="" class="w-5 h-5" />
                    </button>
                  }
                }

                <button
                  type="button"
                  [id]="'file-delete-' + i"
                  class="p-1 transition-opacity hover:opacity-70 cursor-pointer"
                  (click)="removeFile(uploadedFile.id); $event.stopPropagation()"
                  [attr.aria-label]="'Remove ' + uploadedFile.file.name"
                >
                  <img [src]="trashIcon" alt="" class="w-5 h-5" />
                </button>
              </div>
            </div>

            @if (uploadedFile.showPreview && !uploadedFile.error && uploadedFile.previewUrl) {
              <div [id]="'file-preview-' + i" class="px-3 pb-3">
                @if (uploadedFile.file.type.startsWith('image/')) {
                  <img
                    [src]="uploadedFile.previewUrl"
                    [alt]="uploadedFile.file.name"
                    class="max-w-full max-h-64 rounded-lg border border-ld-neutral-200"
                  />
                } @else if (uploadedFile.file.type === 'application/pdf') {
                  <iframe
                    [src]="uploadedFile.safePreviewUrl"
                    [title]="uploadedFile.file.name"
                    class="w-full h-64 rounded-lg border border-ld-neutral-200"
                  ></iframe>
                }
              </div>
            }

            @if (uploadedFile.showEditNotes && !uploadedFile.error) {
              <div [id]="'file-notes-' + i" class="px-3 pb-3">
                <textarea
                  [id]="'file-notes-input-' + i"
                  rows="2"
                  class="w-full px-3 py-2 text-sm font-dm border border-ld-neutral-200 rounded-lg resize-y focus:outline-none focus:ring-1 focus:ring-ld-primary focus:border-ld-primary"
                  [value]="uploadedFile.notes"
                  (input)="onNotesChange(uploadedFile.id, $any($event.target).value)"
                  [placeholder]="t('fileUploadAddNotePlaceholder')"
                  [attr.aria-label]="'Notes for ' + uploadedFile.file.name"
                ></textarea>
              </div>
            }
          </div>
        }
      </div>
    }
  </div>
</ng-container>
